import React, { useEffect } from "react";
import {
  EuiInMemoryTable,
  EuiBasicTableColumn,
  EuiBadgeGroup,
  EuiBadge,
  EuiLink,
} from "@elastic/eui";
import {
  AnketaResourcesEnum,
  KeycloakOrganizations,
  useAnketaI18n,
} from "../..";
import { useNavigate } from "react-router";
import { AnketaCatalogs, AnketaQuestionnaire } from "../types";

export const AnketaCatalogTable = ({
  catalogs,
}: {
  catalogs: Array<AnketaCatalogs>;
}): JSX.Element => {
  const navigate = useNavigate();
  const { translate } = useAnketaI18n();

  const columns: Array<EuiBasicTableColumn<AnketaCatalogs>> = [
    {
      field: "displayName",
      name: translate("anketa.displayName"),
      sortable: true,
      render: (displayName: string, catalog: AnketaCatalogs) => {
        return (
          <EuiLink
            onClick={() =>
              navigate(`/${AnketaResourcesEnum.questionnaires}/${catalog.id}`)
            }
          >
            {displayName}
          </EuiLink>
        );
      },
    },
    {
      field: "questionnaire",
      name: translate("anketa.questionnaires"),
      render: (
        questionnaires: Array<AnketaQuestionnaire>,
        catalog: AnketaCatalogs
      ) => {
        return (
          <EuiBadgeGroup gutterSize="s">
            {questionnaires.map((q) => (
              <EuiBadge
                iconType={"copy"}
                onClick={() => {
                  navigate(
                    `/${AnketaResourcesEnum.questionnaires}/${catalog.id}/${q.id}`
                  );
                }}
                iconOnClick={() => {
                  navigate(
                    `/${AnketaResourcesEnum.questionnaires}/${catalog.id}/${q.id}`
                  );
                }}
                iconOnClickAriaLabel="Navigate to questionnaire"
                onClickAriaLabel="Navigate to questionnaire"
              >
                <EuiLink>{q.displayName}</EuiLink>
              </EuiBadge>
            ))}
          </EuiBadgeGroup>
        );
      },
    },
    {
      field: "organization",
      name: translate("anketa.organization"),
      sortable: true,
      render: (
        organization: Array<KeycloakOrganizations>,
        catalog: AnketaCatalogs
      ) => {
        const allOrgs = catalog.questionnaire
          .flatMap((q) => q.organization)
          .filter((org): org is KeycloakOrganizations => !!org);
        const uniqueOrgs = allOrgs.filter(
          (org, index, self) =>
            self.findIndex(
              (o) => o !== null && org !== null && o.id === org.id
            ) === index
        );
        return (
          <EuiBadgeGroup gutterSize="s">
            {uniqueOrgs.map((org) => (
              <EuiBadge key={org.id}>{org.name}</EuiBadge>
            ))}
          </EuiBadgeGroup>
        );
      },
    },
    {
      field: "members",
      name: translate("anketa.members"),
      render: (members: Array<string>, catalog: AnketaCatalogs) => {
        const allMembers = catalog.questionnaire.flatMap((q) => q.members);
        return (
          <EuiBadgeGroup gutterSize="s">
            {allMembers.map((member) => (
              <EuiBadge iconType={"email"}>{member}</EuiBadge>
            ))}
          </EuiBadgeGroup>
        );
      },
    },
    {
      actions: [
        {
          name: translate("anketa.table.details.share"),
          description: translate("anketa.table.details.share"),
          icon: "copy",
          type: "icon",
          onClick: (item: AnketaCatalogs) => {
            const url = `${window.location.origin}/${AnketaResourcesEnum.questionnaires}/${item.id}`;
            navigator.clipboard.writeText(url);
          },
        },
        {
          name: translate("anketa.table.details"),
          description: translate("anketa.table.details"),
          icon: "eye",
          type: "icon",
          onClick: (item: AnketaCatalogs) => {
            navigate(`/${AnketaResourcesEnum.questionnaires}/${item.id}`);
          },
        },
      ],
    },
  ];

  useEffect(() => {
    if (catalogs.length === 1) {
      navigate(`/${AnketaResourcesEnum.questionnaires}/${catalogs[0].id}`);
    }
  }, [catalogs]);

  return (
    <EuiInMemoryTable items={catalogs} columns={columns} pagination={true} />
  );
};
